/**
 * Approvals (A04/A09 epics + QM-D19-002). The list links to /approvals/:id; notification and email deep
 * links open the same route on mobile for one-tap approve/reject. Cross-tenant ids come back 404 from the
 * API and render as "not found" (no existence leak). An expired session is handled by the BFF observer:
 * sign-in, then back to this item via ?next=.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import type { ApprovalItem } from '@qm/contracts/approvals';
import { Badge, Button, EmptyState, ErrorState, Heading, Loading, PageHeader, Panel, Text } from '@qm/ui';
import { useApp } from '../../../lib/app-context';
import { BffError, bffJson } from '../../../lib/bff';
import { useFormat } from '../../../lib/format';
import { useResource } from '../../../lib/resource';
import { rowsOf, str, useWorkspaceName } from '../_shared/ListScreen';
import { distinct, SelectTable } from '../_shared/SelectTable';
import { knownKind } from '../home/home-data';
import '../_shared/customer.css';

const STATUSES = ['pending', 'approved', 'rejected', 'executed'];

export function ApprovalsPage() {
  const { t } = useApp();
  const f = useFormat();
  const res = useResource<unknown>('/v1/approvals/items');
  const [filters, setFilters] = useState({ status: '', kind: '' });
  const [selected, setSelected] = useState<string | null>(null);
  const rows = res.status === 'ready' ? rowsOf(res.data) : [];
  const shown = rows.filter((r) => (!filters.status || str(r.status) === filters.status) && (!filters.kind || str(r.kind) === filters.kind));
  const current = rows.find((r) => r.id === selected) ?? null;
  const statusLabel = (v: string) => (STATUSES.includes(v) ? t(`shell.approval.status.${v}`) : v);
  const kindLabel = (v: string) => t(`shell.home.kind.${knownKind(v)}`);
  const set = (k: keyof typeof filters) => (e: React.ChangeEvent<HTMLSelectElement>) => setFilters({ ...filters, [k]: e.target.value });
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <PageHeader eyebrow={useWorkspaceName()} title={t('shell.screens.approvals.title')} intro={t('shell.screens.approvals.intro')} />
      {res.status === 'loading' ? <Loading label={t('common.loading')} /> : res.status === 'error' ? <ErrorState message={t('shell.error.load')} /> : !rows.length ? <EmptyState title={t('shell.screens.approvals.empty')} /> : (
        <div className={current ? 'qs-split' : undefined}>
          <div className="cu-main">
            <div className="qs-filters" role="group" aria-label={t('approvals.list.filters')}>
              <label className="cu-field"><span>{t('shell.col.status')}</span>
                <select value={filters.status} onChange={set('status')}><option value="">{t('calendar.delivery.all')}</option>{distinct(rows, 'status').map((v) => <option key={v} value={v}>{statusLabel(v)}</option>)}</select></label>
              <label className="cu-field"><span>{t('shell.col.type')}</span>
                <select value={filters.kind} onChange={set('kind')}><option value="">{t('calendar.delivery.all')}</option>{distinct(rows, 'kind').map((v) => <option key={v} value={v}>{kindLabel(v)}</option>)}</select></label>
            </div>
            {shown.length ? (
              <SelectTable caption={t('shell.screens.approvals.title')} rows={shown} selected={selected} onSelect={setSelected} columns={[
                { key: 'title', header: t('shell.col.title'), primary: true, render: (r) => <bdi>{f.text(r.title)}</bdi> },
                { key: 'kind', header: t('shell.col.type'), render: (r) => kindLabel(str(r.kind)) },
                { key: 'status', header: t('shell.col.status'), render: (r) => <Badge tone={r.status === 'pending' ? 'accent' : 'neutral'}>{statusLabel(str(r.status))}</Badge> },
                { key: 'createdAt', header: t('shell.col.created'), render: (r) => f.date(str(r.createdAt)) },
              ]} />
            ) : <EmptyState title={t('approvals.list.noMatch')} />}
          </div>
          {current ? (
            <aside className="qs-aside" aria-labelledby="approval-detail" data-testid="approval-detail">
              <div className="qs-aside__head"><h2 id="approval-detail"><bdi>{f.text(current.title)}</bdi></h2>
                <button type="button" className="cu-btn" data-variant="outline" onClick={() => setSelected(null)}>{t('approvals.list.close')}</button></div>
              <ul className="qs-kv">
                <li><span className="qs-kv__label">{t('shell.col.type')}</span><span className="qs-kv__value">{kindLabel(str(current.kind))}</span></li>
                <li><span className="qs-kv__label">{t('shell.col.status')}</span><span className="qs-kv__value"><Badge tone={current.status === 'pending' ? 'accent' : 'neutral'}>{statusLabel(str(current.status))}</Badge></span></li>
                <li><span className="qs-kv__label">{t('shell.col.created')}</span><span className="qs-kv__value">{f.dateTime(str(current.createdAt))}</span></li>
              </ul>
              <Link className="cu-btn" to={`/approvals/${encodeURIComponent(current.id)}`}>{t('approvals.list.open')}</Link>
            </aside>
          ) : null}
        </div>
      )}
    </section>
  );
}

export function ApprovalDetailPage() {
  const { id = '' } = useParams();
  const { t } = useApp();
  const f = useFormat();
  const res = useResource<ApprovalItem>(`/v1/approvals/items/${encodeURIComponent(id)}`);
  const [decided, setDecided] = useState<ApprovalItem | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const decide = async (d: 'approve' | 'reject') => {
    setBusy(true); setError(null);
    try {
      setDecided(await bffJson<ApprovalItem>(`/v1/approvals/items/${encodeURIComponent(id)}/${d}`, 'POST', {}));
    } catch (e) {
      setError(e instanceof BffError && e.status === 403 ? (permissionDenied(e, t) ?? t('shell.approval.noPermission')) : t('shell.error.action'));
    } finally { setBusy(false); }
  };

  if (res.status !== 'ready') {
    return (
      <section className="qm-page" aria-labelledby="page-title">
        <Link to="/approvals">{t('shell.approval.back')}</Link>
        <Heading level={1} id="page-title">{t('shell.approval.title')}</Heading>
        {res.status === 'loading' ? <Loading label={t('common.loading')} /> : res.error instanceof BffError && res.error.status === 404
          ? <EmptyState title={t('shell.approval.notFound')} />
          : <ErrorState message={t('shell.error.load')} />}
      </section>
    );
  }
  const item = decided ?? res.data;
  return (
    <section className="qm-page" aria-labelledby="page-title">
      <Link to="/approvals">{t('shell.approval.back')}</Link>
      <Heading level={1} id="page-title"><bdi>{f.text(item.title)}</bdi></Heading>
      <Panel>
        <Text><Badge tone={item.status === 'pending' ? 'accent' : 'neutral'}>{t(`shell.approval.status.${item.status}`)}</Badge></Text>
        <Text tone="muted">{t('shell.approval.requested', { when: f.dateTime(item.createdAt) })}</Text>
        {item.decidedAt ? <Text role="status">{t('shell.approval.decided', { when: f.dateTime(item.decidedAt) })}</Text> : null}
      </Panel>
      {item.status === 'pending' ? (
        <div className="qm-row">
          <Button variant="primary" disabled={busy} onClick={() => void decide('approve')}>{t('shell.approval.approve')}</Button>
          <Button disabled={busy} onClick={() => void decide('reject')}>{t('shell.approval.reject')}</Button>
        </div>
      ) : null}
      {error ? <ErrorState message={error} /> : null}
    </section>
  );
}

/** The agent-permission refusal (flag ai.agent_rbac) names the missing permission; show it in the user's language. */
export function permissionDenied(e: BffError, t: (k: string, p?: Record<string, string | number>) => string): string | null {
  const d = (e.envelope?.error as { details?: { reason?: string; missing?: string[] } } | undefined)?.details;
  if (d?.reason !== 'permission_denied' || !d.missing?.length) return null;
  return t('approvals.envelope.permission_denied', { permission: d.missing.map((m) => t(`approvals.permission.${m}`)).join(' + ') });
}
